import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  addCrossClientClients,
  addCrossClientFetchedClientIds,
  selectCrossClientClients,
  selectCrossClientFetchedClientIds,
  selectCrossClientFilteredResults,
  selectCrossClientResultsExecution,
  setCrossClientClientsLoading,
} from '../../../store/crossClient/crossClientResults'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { useLazyGetAllUsersQuery } from '../../../store/user/user'
import { selectAccountId } from '../../../store/auth/authSlice'
import { User } from '../../../models/User'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

export const useExportCrossClientResults = () => {
  const { tracked } = useTracking()

  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const results = useAppSelector(selectCrossClientFilteredResults)
  const clients = useAppSelector(selectCrossClientClients)
  const fetchedClientIds = useAppSelector(selectCrossClientFetchedClientIds)

  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()

  const disabled = !results.length

  const fetchMissingClients = async () => {
    const missingClientIds = results.map((result) => result.clientId).filter((clientId) => !fetchedClientIds[clientId])
    if (!missingClientIds.length) {
      return {}
    }
    dispatch(setCrossClientClientsLoading(true))
    dispatch(addCrossClientFetchedClientIds(missingClientIds))

    const missingClients: Record<string, User> = {}
    try {
      const chunkSize = 500
      const chunks = Array.from({ length: Math.ceil(missingClientIds.length / chunkSize) }, (_, index) =>
        missingClientIds.slice(index * chunkSize, (index + 1) * chunkSize),
      )
      for (const chunk of chunks) {
        const chunkContacts = await triggerGetAllUsers({ accountId, ids: chunk }).unwrap()
        if (chunkContacts?.data) {
          const chunkClients = chunkContacts.data.filter((contact) => contact.roles.some((role) => role.name === 'Client'))
          dispatch(addCrossClientClients(chunkClients))
          chunkClients.reduce((acc, client) => {
            acc[client.id] = client
            return acc
          }, missingClients)
        }
      }
    } finally {
      dispatch(setCrossClientClientsLoading(false))
    }
    return missingClients
  }

  const exportCSV = tracked(() => _exportCSV(), {
    eventName: TrackEvent.X_CLIENT_RESULT_EXPORTED,
    eventDetails: {
      executionId: execution?.id,
    },
  })

  const _exportCSV = async () => {
    if (disabled) {
      return
    }

    const missingClients = await fetchMissingClients()

    const allClients = { ...clients, ...missingClients }

    const csvRows = [
      ['Client', 'Email', 'Phone Number'],
      ...results.map((result) => {
        const client = allClients[result.clientId]
        const clientName = client ? getUserDisplayName(client) : 'Unknown'
        const clientEmail = client ? client.email : 'Unknown'
        const clientPhoneNumber = client && client.phoneNumber ? client.phoneNumber : ''

        return [clientName, clientEmail, clientPhoneNumber]
      }),
    ]

    const csvContent = csvRows.map((row) => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `CrossClientResults_${execution?.id}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return {
    disabled,
    exportCSV,
  }
}
